import * as Sentry from "@sentry/react";
import { type ErrorComponentProps, useRouter } from "@tanstack/react-router";
import { IconButton } from "@/components/ui/icon-button";
import { getApiErrorRequestContext, isCancellationError, shouldReportFrontendError } from "@/lib/error-classification";
import { Bug, Home, Mail, RotateCcw } from "lucide-react";
import { useEffect, useState } from "react";
import { openBugReport, openSupportEmail } from "@/lib/support-actions";

const cancellationRecoveryAttempts = new Map<string, number>();
const cancellationRecoveryWindowMs = 5000;

const shouldRecoverCancellationError = (routeKey: string) => {
  const now = Date.now();
  const lastAttempt = cancellationRecoveryAttempts.get(routeKey);

  if (lastAttempt && now - lastAttempt < cancellationRecoveryWindowMs) {
    return false;
  }

  cancellationRecoveryAttempts.set(routeKey, now);
  return true;
};

const getErrorMessage = (error: unknown) => {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Something went wrong.";
};

const buildErrorSupportBody = (message: string, error: unknown) => {
  const apiRequest = getApiErrorRequestContext(error);

  return [message, apiRequest ? `API request: ${apiRequest.method} ${apiRequest.path}` : null]
    .filter(Boolean)
    .join("\n");
};

export const RootErrorComponent = ({ error, reset }: ErrorComponentProps) => {
  const [eventId, setEventId] = useState<string | null>(null);
  const [renderCancellationError, setRenderCancellationError] = useState(false);
  const router = useRouter();
  const isCancelled = isCancellationError(error);
  const routeKey = router.state.location.href;
  const message = getErrorMessage(error);
  const apiRequest = getApiErrorRequestContext(error);
  const supportBody = buildErrorSupportBody(message, error);

  useEffect(() => {
    if (!isCancelled) {
      setRenderCancellationError(false);
      return;
    }

    if (!shouldRecoverCancellationError(routeKey)) {
      setRenderCancellationError(true);
      return;
    }

    reset();
    void router.invalidate();
  }, [isCancelled, reset, routeKey, router]);

  useEffect(() => {
    if (!import.meta.env.PROD || !Sentry.getClient() || !shouldReportFrontendError(error)) {
      return;
    }

    const capturedEventId = Sentry.captureException(error, {
      tags: {
        errorBoundary: "root-route",
      },
    });

    setEventId(capturedEventId);
  }, [error]);

  return isCancelled && !renderCancellationError ? null : (
    <main className="flex flex-1 items-center justify-center px-4 py-16">
      <div className="w-full max-w-lg rounded-2xl border border-border bg-card p-6 shadow-sm">
        <h1 className="text-xl font-semibold text-card-foreground">Something went wrong</h1>
        <p className="mt-3 text-sm text-muted-foreground">{message}</p>
        <p className="mt-3 text-sm text-muted-foreground">
          If this interrupted what you were doing, please add a few details to the bug report or email support directly.
        </p>
        <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <IconButton
            className="w-full"
            icon={<RotateCcw />}
            onClick={() => {
              window.location.reload();
            }}
            type="button"
          >
            Try again
          </IconButton>
          <IconButton
            className="w-full"
            icon={<Home />}
            onClick={() => {
              window.location.assign("/");
            }}
            type="button"
            variant="outline"
          >
            Go home
          </IconButton>
          <IconButton
            className="w-full"
            icon={<Bug />}
            onClick={() => {
              void openBugReport({
                backendRequestId: apiRequest?.requestId,
                body: `I saw this error in Veronics:\n\n${supportBody}\n\nI was trying to:`,
                eventId,
                subject: "Veronics bug report",
              });
            }}
            type="button"
            variant="outline"
          >
            Report bug
          </IconButton>
          <IconButton
            className="w-full"
            icon={<Mail />}
            onClick={() => {
              openSupportEmail({
                backendRequestId: apiRequest?.requestId,
                body: `I need help with this Veronics error:\n\n${supportBody}\n\nI was trying to:`,
                eventId,
              });
            }}
            type="button"
            variant="outline"
          >
            Email support
          </IconButton>
        </div>
      </div>
    </main>
  );
};
